import {formatDate} from "../time/TimeCalculator";
import {Base} from "../Base";
import {arrayLast, prefixUrl} from "../util";

/**
 * A fetcher for layer timeseries
 * @extends Base
 */
class TimeseriesFetcher extends Base {

    randomData = false;

    /**
     *
     * @param dataSeriesUrl {string}
     * @param layer {Layer} The layer to fetch data for
     * @param coordsWgs84 {Array<number>} The coordinates to fetch data for
     */
    constructor(dataSeriesUrl, layer, coordsWgs84) {
        super();
        this.dataSeriesUrl = prefixUrl(dataSeriesUrl);
        this.layer = layer;
        this.coordsWgs84 = coordsWgs84;
        this.cache = {};
        this.catalogue = undefined;
    }

    getClassName() {
        return "TimeseriesFetcher";
    }

    /**
     * Load data from the server
     * @param from {Date} Fetch data from
     * @param until {Date} Fetch data until
     * @param unboundedFrom {Date} Fill data with null between this and from. If undefined, no filling will happen
     * @param unboundedUntil {Date} Fill data with null between until and this. If undefined, no filling will happen
     * @param onLoad {function} A callback that receives a map of series and a boolean that will be true if the data is random
     */
    loadData(from, until, unboundedFrom = from, unboundedUntil = until, onLoad) {
        this._fetch(from, until, fetchedValues => {
            if (!fetchedValues) {
                onLoad();
            } else {
                const result = {};
                for (let seriesCode in fetchedValues) {
                    if (seriesCode !== 'dates') {
                        const values = {};
                        for (let i = 0; i < fetchedValues.dates.length; i++) {
                            values[formatDate(fetchedValues.dates[i])] = fetchedValues[seriesCode][i];
                        }
                        const {dates, processedValues} = this._processData(
                            values,
                            unboundedFrom,
                            unboundedUntil === until ? until : unboundedUntil
                        );
                        result[seriesCode] = {x: dates, y: processedValues}
                    }
                }
                onLoad(result, this.randomData);
            }
        });
    }

    _generateRandomData(from, until, onFetch) {
        this.logInfo('Will generate a time series for service: ' + this.layer.getServiceConfig().getCode() + ': from ' + formatDate(from) + ' until ' + formatDate(until));
        const randNr = Math.random();
        if (randNr > 0 && randNr <= 0.0) { //change values to increase probability of an error (testing purposes)
            throw 'ERROR';
        }
        const delay = (Math.random() + Math.random() * .7) * 1000;
        setTimeout(() => {
            const timeCalculator = this.layer.getTimeCalculator();
            const chartStyler = this.layer.getChartStyler();
            const colorscale = chartStyler.getPlotlyColorscale();
            const range = this.getRangeForRandomData(chartStyler, colorscale);
            const fetchedValues = {};
            const timeScales = this.layer.getServiceConfig().getTimeScales();
            if (timeScales && timeScales.length > 0) {
                timeScales.forEach(timeScale => {
                    const product = this.layer.getProductConfigForTimeScale(timeScale);
                    fetchedValues[product.getCode()] = timeCalculator.getSampleData(from, until, range);
                });
            } else {
                fetchedValues[this.layer.getProductConfig().getCode()] = timeCalculator.getSampleData(from, until, range);
            }
            fetchedValues.dates = timeCalculator.getTimeSeries(timeCalculator.fixDate(from), until);
            this.logDebug('Just generated data for service: ' + this.layer.getServiceConfig().getCode() + ': from ' + formatDate(from) + ' until ' + formatDate(until), fetchedValues);
            onFetch(fetchedValues);
        }, delay);
    }

    getRangeForRandomData(chartStyler, colorscale) {
        if (chartStyler.isClassified()) {
            return colorscale.map(c => c[0]);
        }
        const min = colorscale[0][0] === undefined
            ? colorscale[1][0] - Math.abs(colorscale[1][0]) * .5
            : colorscale[0][0];
        const max = arrayLast(colorscale)[0] === undefined
            ? colorscale[colorscale.length - 2][0] + Math.abs(colorscale[colorscale.length - 2][0]) * .5
            : arrayLast(colorscale)[0];
        return [min, max];
    }

    _handleFetchedValues(onFetch, data, from, until) {
        let hasError = false;
        const error = (errMsg) => {
            if (errMsg)
                this.logError(errMsg);
            onFetch(undefined);
            hasError = true;
        }
        if (!data.dates) {
            error("No dates")
            return;
        }

        const dates = this.layer.getTimeCalculator().getTimeSeries(from, until);

        const fetchedValues = {};
        const timeScales = this.layer.getServiceConfig().getTimeScales();
        const timeSeriesService = this.catalogue.services.hst[this.layer.getServiceConfig().getCode()];
        if (timeScales && timeScales.length > 0) {
            timeScales.every(timeScale => {
                const product = this.layer.getProductConfigForTimeScale(timeScale);
                const dataSeriesCodeForTimescaleKey = timeSeriesService.inds[timeSeriesService.prds.indexOf(product.getCode())];
                if (!data[dataSeriesCodeForTimescaleKey]) {
                    error("No data for code " + dataSeriesCodeForTimescaleKey);
                    return false;
                }

                if (data.dates.length !== data[dataSeriesCodeForTimescaleKey].length) {
                    error("Dates and values have different lengths (" + dataSeriesCodeForTimescaleKey + ": " + data[dataSeriesCodeForTimescaleKey].length + ")");
                    return false;
                }
                const values = [];
                for (const date of dates) {
                    const index = data.dates.indexOf(formatDate(date));
                    if (index >= 0)
                        values.push(data[dataSeriesCodeForTimescaleKey][index])
                    else values.push(null);
                }
                fetchedValues[product.getCode()] = values;
                return true;
            });
            if (hasError)
                return;
        } else {
            const dataSeriesCode = timeSeriesService.inds[timeSeriesService.prds.indexOf(this.layer.getProductConfig().getCode())];
            if (!data[dataSeriesCode] || !data.dates || data.dates.length !== data[dataSeriesCode].length) {
                error("Dates and values have different lengths (" + dataSeriesCode + ")");
                return;
            }

            const values = [];
            for (const date of dates) {
                const index = data.dates.indexOf(formatDate(date));
                if (index >= 0)
                    values.push(data[dataSeriesCode][index])
                else values.push(null);
            }
            fetchedValues[this.layer.getProductConfig().getCode()] = values;
        }
        fetchedValues.dates = dates;
        this.logDebug('Just fetched data for service: ' + this.layer.getServiceConfig().getCode() + ': from ' + formatDate(from) + ' until ' + formatDate(until), fetchedValues);
        onFetch(fetchedValues);
    }

    _fetchFromServer(from, until, onFetch) {
        this.logInfo('Will fetch a time series for service: ' + this.layer.getServiceConfig().getCode() + ': from ' + formatDate(from) + ' until ' + formatDate(until));
        const prdCodes = this.layer.getServiceConfig().getTimeScales().length > 0
            ? this.layer.getServiceConfig().getTimeScales().map(ts => this.layer.getProductConfigForTimeScale(ts).getCode())
            : [this.layer.getProductConfig().getCode()];

        const uri = this.dataSeriesUrl.format([
            this.coordsWgs84[0] + "",
            this.coordsWgs84[1] + "",
            tumbo.appCode,
            this.layer.getServiceConfig().getCode(),
            formatDate(from),
            formatDate(until),
            prdCodes.join(',')
        ]);
        if(!this.catalogue)
            this.catalogue = {
                services: {
                    hst: {}
                }
            };
        this.catalogue.services.hst[this.layer.getServiceConfig().getCode()] = {
            inds: prdCodes,
            prds: prdCodes
        }
        $.getJSON(uri, data => this._handleFetchedValues(onFetch, data, from, until));
    }

    _getData(from, until, onFetch) {
        if (!this.randomData) {
            this._fetchFromServer(from, until, onFetch)
        } else {
            this._generateRandomData(from, until, onFetch);
        }
    }

    _fetch(from, until, onFetch) {
        try {
            const returnValues = () => onFetch(this._clipCache(from, until));

            if (!this.cache.from || !this.cache.until) { //no cache yet
                this._getData(from, until, data => {
                    if (data)
                        this._cacheData(data);
                    returnValues();
                });
            } else {
                const fromAheadOfCache = from > this.cache.until;
                const untilBehindOfCache = until < this.cache.from;
                if (fromAheadOfCache) {
                    this._getData(this.cache.until, until, data => {
                        this._appendCacheData(data);
                        returnValues();
                    });
                } else if (untilBehindOfCache) {
                    this._getData(from, this.cache.from, data => {
                        this._prependCacheData(data);
                        returnValues();
                    });
                } else { //requested frame overlaps cache
                    const fromBeforeCache = from < this.cache.from;
                    const untilAfterCache = until > this.cache.until;
                    if (!fromBeforeCache && !untilAfterCache) { //requested frame is within cache
                        returnValues();
                    } else {
                        const effectiveFrom = fromBeforeCache ? from : this.cache.until;
                        const effectiveUntil = untilAfterCache ? until : this.cache.from;
                        this._getData(effectiveFrom, effectiveUntil, data => {
                            if (fromBeforeCache)
                                this._prependCacheData(data);
                            if (untilAfterCache)
                                this._appendCacheData(data);
                            returnValues();
                        });
                    }
                }
            }
        } catch (e) {
            this._onFetchError(from, until, e, onFetch);
        }
    }

    _onFetchError(from, until, e, onFetch) {
        this.logError("Error reading data for layer {0} on coords ({1},{2}) from {3} until {4}".format(
            [this.layer.getCode(), this.coordsWgs84[0] + "", this.coordsWgs84[1] + "", formatDate(from), formatDate(until)]
        ), e);
        onFetch(undefined);
    }

    _clipCache(from, until) {
        //if cache does not contain data for the current product we're in an invalid state
        if (!this.cache[this.layer.getProductConfig().getCode()]) {
            this.logError("No cache data for product " + this.layer.getProductConfig().getCode());
            return undefined;
        }
        // //if cache does not contain the time frame we're in an invalid state
        // if (!this.cache.from || !this.cache.until
        //     || from < this.cache.from || from > this.cache.until
        //     || until > this.cache.until || until < this.cache.from) {
        //     this.logError("We need data from {0} until {1}, bue cache only has data from {2} until {3}".format(
        //         [formatDate(from),
        //             formatDate(until),
        //             formatDate(this.cache.from),
        //             formatDate(this.cache.until)]
        //     ))
        //     return undefined;
        // }

        const result = {};
        const timeCalculator = this.layer.getTimeCalculator();
        const start = Math.max(0, this.cache.dates.map(d => d.getTime()).indexOf(timeCalculator.fixDate(from).getTime()));
        const end = this.cache.dates.map(d => d.getTime()).indexOf(timeCalculator.fixDate(until).getTime()) + 1;//we want the first date of the last period
        for (let cacheProp in this.cache) {
            if (cacheProp !== 'from' && cacheProp !== 'until' && cacheProp !== 'untilPeriodStart' && cacheProp !== 'dates') {
                result[cacheProp] = this.cache[cacheProp].slice(start, end ? end : undefined);
            }
        }
        result.dates = timeCalculator.getTimeSeries(this.cache.dates[start], this.cache.dates[(end ? end : this.cache.dates.length) - 1], true);
        return result;
    }

    _cacheData(data) {
        if (!data.dates || data.dates.length === 0)
            return;
        this.cache.from = this.layer.getTimeCalculator().fixDate(data.dates[0]);
        this.cache.untilPeriodStart = arrayLast(data.dates);
        this.cache.until = this.layer.getTimeCalculator().fixUnboundedDateUntil(this.cache.untilPeriodStart);
        for (let cacheProp in data) {
            if (cacheProp !== 'from' && cacheProp !== 'until' && cacheProp !== 'untilPeriodStart') {
                this.cache[cacheProp] = data[cacheProp];
            }
        }
    }

    _prependCacheData(data) {
        const sliceEnd = data.dates.map(d => d.getTime()).indexOf(this.cache.from.getTime());
        this.cache.from = data.dates[0];
        for (let cacheProp in this.cache) {
            if (cacheProp !== 'from' && cacheProp !== 'until' && cacheProp !== 'untilPeriodStart') {
                this.cache[cacheProp] = data[cacheProp].slice(0, sliceEnd).concat(this.cache[cacheProp]);
            }
        }
    }

    _appendCacheData(data) {
        if (!data || !data.dates || data.dates.length === 0)
            return;
        const sliceStart = data.dates.map(d => d.getTime()).indexOf(this.cache.untilPeriodStart.getTime()) + 1;
        this.cache.untilPeriodStart = arrayLast(data.dates);
        this.cache.until = this.layer.getTimeCalculator().fixUnboundedDateUntil(this.cache.untilPeriodStart);
        for (let cacheProp in this.cache) {
            if (cacheProp !== 'from' && cacheProp !== 'until' && cacheProp !== 'untilPeriodStart') {
                this.cache[cacheProp] = this.cache[cacheProp].concat(data[cacheProp].slice(sliceStart));
            }
        }
    }

    //force unboundedFrom and unboundedTo
    _processData(originalValues, unboundedFrom, unboundedUntil) {
        const timeCalculator = this.layer.getTimeCalculator();
        const dates = [], processedValues = [];
        timeCalculator.getTimeSeries(timeCalculator.fixDate(unboundedFrom), unboundedUntil).forEach(date => {
            const formattedDate = formatDate(date);
            dates.push(date);
            if (originalValues[formattedDate] !== null && originalValues[formattedDate] !== undefined)
                processedValues.push(originalValues[formattedDate]);
            else processedValues.push(null);
        });
        return {dates, processedValues};
    }
}

class FeatureTimeseriesFetcher extends TimeseriesFetcher {
    constructor(layer, featureId) {
        super(undefined, layer, undefined);
        this.featureId = featureId;
    }

    getClassName() {
        return "FeatureTimeseriesFetcher";
    }

    _fetchFromServer(from, until, onFetch) {
        const baseUrl = prefixUrl('/services/hst');
        const url = new URL('https:' + baseUrl);
        const prodCode = this.layer.getProductConfig().getCode();
        url.searchParams.set('appCode', tumbo.appCode);
        url.searchParams.set('srvs', prodCode);
        url.searchParams.set('id', this.featureId);
        url.searchParams.set('date_ini', formatDate(from));
        url.searchParams.set('date_end', formatDate(until));
        if(!this.catalogue)
            this.catalogue = {
                services: {
                    hst: {}
                }
            };
        this.catalogue.services.hst[this.layer.getServiceConfig().getCode()] = {
            inds: [prodCode],
            prds: [prodCode]
        }
        $.getJSON(baseUrl + '?' + url.searchParams.toString(), data => {
            const res = {dates: data.dates};
            res[prodCode] = data[this.layer.getChartStyler().getAttributeName()];
            this._handleFetchedValues(onFetch, res, from, until);
        })
    }

    _onFetchError(from, until, e, onFetch) {
        this.logError("Error reading data for feature layer {0} from {1} until {2}".format(
            [this.layer.getCode(), formatDate(from), formatDate(until)]
        ), e);
        onFetch(undefined);
    }
}

export {TimeseriesFetcher, FeatureTimeseriesFetcher}